import { zodResolver } from "@hookform/resolvers/zod";
import { Anchor, Button, Card, Divider, Image, LoadingOverlay, Stack, Title } from "@mantine/core";
import { AtIcon, KeyIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import z from "zod";

import nextLogo from "@/assets/logo.png";
import { Input } from "@/components/_Input";
import { SideBySide } from "@/components/SideBySide";
import { TOASTER_ID_KEYS } from "@/constants";
import { useRecaptcha, useWindowLocationHost } from "@/hooks";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { useAuth, useGetCompanyByHostQuery } from "@/store";

type Schema = z.infer<typeof schema>;
const schema = z.object({
  email: z.email("Email inválido").nonempty("Email é obrigatório"),
  password: z.string().nonempty("Senha é obrigatória")
});

export const Route = createFileRoute("/_auth/login")({
  component: RouteComponent,
  head: () => ({
    meta: [{ title: "Login - Dashboard" }]
  })
});

function RouteComponent() {
  const { SignIn } = useAuth();

  const recaptcha = useRecaptcha();
  const { windowLocationHost: host } = useWindowLocationHost();
  const { data: company, isLoading } = useGetCompanyByHostQuery({ host }, { skip: !host });

  const {
    reset,
    handleSubmit: handleSubmitForm,
    control,
    formState: { isSubmitting }
  } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: ""
    },
    mode: "all"
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { email, password } = data;

    await SignIn(
      {
        email,
        password,
        company: company ? { id: company?.id } : null,
        recaptcha_token: recaptcha
      },
      {
        onSuccess: () => {
          toast.success("Login realizado com sucesso!", { toasterId: TOASTER_ID_KEYS.AUTH.LOGIN_SUCCESS });
          reset();
        }
      }
    );
  };

  const handleTermModal = () => {
    MODAL_HANDLER.terms();
  };

  if (isLoading) {
    return <LoadingOverlay overlayProps={{ backgroundOpacity: 0.1, blur: 1 }} loaderProps={{ type: "nextCargo" }} visible />;
  }

  return (
    <>
      <SideBySide.Primary>
        <Image src={`${import.meta.env.VITE_APP_API_URL}/${company?.logo}`} fallbackSrc={nextLogo} className={`mb-2 block w-75! md:hidden!`} />

        <Card shadow="lg" radius="lg" p="xl" withBorder>
          <Stack>
            <Title order={1} c="gray.8">
              Entrar na sua conta
            </Title>

            <form onSubmit={handleSubmitForm(onSubmit)} className="flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                <Input.Text label="Email" description="Digite seu email" name="email" leftSection={<AtIcon weight="bold" />} placeholder="Email" control={control} />

                <Input.Password label="Senha" description="Digite sua senha" name="password" leftSection={<KeyIcon weight="bold" />} placeholder="Senha" control={control} />
              </div>

              <Button type="submit" color="primary" loading={isSubmitting} fullWidth>
                Entrar
              </Button>
            </form>

            <Divider />

            <Anchor className="max-w-72 cursor-pointer self-center text-center font-medium text-gray-400 hover:underline" role="button" onClick={handleTermModal}>
              Leia nossa politica de Privacidade e <br /> Termos de Uso
            </Anchor>

            {/* <ReCAPTCHA ref={recaptchaRef} sitekey={sitekey} onChange={handleRecaptchaChange} className="self-center" style={{ width: "100%" }} /> */}
          </Stack>
        </Card>
      </SideBySide.Primary>

      <SideBySide.Secondary>
        <Image src={`${import.meta.env.VITE_APP_API_URL}/${company?.logo}`} fallbackSrc={nextLogo} className={`w-100! xl:w-137.5!`} />
      </SideBySide.Secondary>
    </>
  );
}
